import type { ReactElement } from 'react';
import { Box, Divider, Group, Stack, Text } from '@mantine/core';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { SurfaceCard } from '@/design/SurfaceCard';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { formatCompact, formatPercent, formatUsd } from '@/shared/analytics/MetricFormatting';

interface EvidenceTokenBreakdownPanelProps {
    records: InteractionRecord[];
}

const IN_FILL = 'var(--bm-chart-1)';
const OUT_FILL = 'var(--bm-chart-2)';

/**
 * The consumption drill-in's token graphic: the slice's input-vs-output token
 * split (a breakdown that stays meaningful on every slice, including the
 * single-model drill), with the allocated cost and cost-per-1K-tokens below it.
 * Computed client-side from the loaded evidence records; renders nothing when
 * the slice carries no token volume.
 */
export default function EvidenceTokenBreakdownPanel({ records }: EvidenceTokenBreakdownPanelProps): ReactElement | null {
    let inputTokens = 0;
    let outputTokens = 0;
    let cost = 0;
    for (const record of records) {
        inputTokens += record.inputTokens;
        outputTokens += record.outputTokens;
        cost += record.allocatedCostUsd;
    }
    const total = inputTokens + outputTokens;
    if (total === 0) return null;

    const slices = [
        { name: 'Input', value: inputTokens, fill: IN_FILL },
        { name: 'Output', value: outputTokens, fill: OUT_FILL },
    ];
    const costPerThousand = total > 0 ? (cost / total) * 1000 : 0;

    return (
        <SurfaceCard $padding="md">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" mb="sm" style={{ letterSpacing: '0.06em' }}>
                Tokens & cost
            </Text>
            <Box style={{ position: 'relative', height: 150 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie data={slices} dataKey="value" nameKey="name" innerRadius={46} outerRadius={64} paddingAngle={2} stroke="none" isAnimationActive={false}>
                            {slices.map((slice) => (
                                <Cell key={slice.name} fill={slice.fill} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                <Box style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                    <Text fw={700} ff="monospace" style={{ fontSize: '16px', lineHeight: 1 }}>
                        {formatCompact(total)}
                    </Text>
                    <Text size="xs" c="dimmed" mt={2}>
                        total tokens
                    </Text>
                </Box>
            </Box>
            <Stack gap={4} mt="sm">
                {slices.map((slice) => (
                    <Group key={slice.name} gap={8} wrap="nowrap" justify="space-between">
                        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: slice.fill, flexShrink: 0 }} />
                            <Text size="xs" truncate>
                                {slice.name}
                            </Text>
                        </Group>
                        <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                            {formatCompact(slice.value)} · {formatPercent(slice.value / total)}
                        </Text>
                    </Group>
                ))}
            </Stack>
            <Divider my="sm" color="var(--bm-color-border)" />
            <Stack gap={4}>
                <Group gap={8} wrap="nowrap" justify="space-between">
                    <Text size="xs" c="dimmed">
                        Cost
                    </Text>
                    <Text size="xs" ff="monospace" fw={600} style={{ flexShrink: 0 }}>
                        {cost > 0 ? formatUsd(cost) : '—'}
                    </Text>
                </Group>
                <Group gap={8} wrap="nowrap" justify="space-between">
                    <Text size="xs" c="dimmed">
                        Cost / 1K tokens
                    </Text>
                    <Text size="xs" ff="monospace" c="dimmed" style={{ flexShrink: 0 }}>
                        {cost > 0 ? formatUsd(costPerThousand) : '—'}
                    </Text>
                </Group>
            </Stack>
        </SurfaceCard>
    );
}
